'use client';

// ui-1.4-2: Modal de comparação com precedente (Copilot · IA).
// Fiel ao ComparacaoPrecedenteModal do protótipo (design-import/src/pages/inicio.jsx),
// portado para o design system. Dados 100% reais via GET /api/ia/dashboard/comparacao
// (metadados, seções por embedding, lições das atividades) — sem invenção.

import { useEffect, useState } from 'react';
import { Modal, Button, Chip, type ChipVariant } from '@/components/ui';
import {
  buscarComparacaoPrecedente,
  fmtMoney,
  type Comparacao,
  type ComparacaoMeta,
  type CompMatch,
} from '@/lib/dashboard-api';

const MATCH_META: Record<CompMatch, { variant: ChipVariant; label: string; icon: string }> = {
  igual: { variant: 'success', label: 'Idêntico', icon: 'pi-check' },
  reusar: { variant: 'ai', label: 'Reusar', icon: 'pi-copy' },
  ajustar: { variant: 'warning', label: 'Ajustar', icon: 'pi-pencil' },
  parcial: { variant: 'info', label: 'Adaptar', icon: 'pi-sliders-h' },
};

const LICAO_ICON: Record<string, string> = {
  doc_aprovado: 'pi-check-circle',
  doc_rejeitado: 'pi-times-circle',
  doc_gerado: 'pi-sparkles',
  doc_regenerado: 'pi-sync',
};

function MetaCard({
  meta,
  eyebrow,
  cor,
}: {
  meta: ComparacaoMeta;
  eyebrow: string;
  cor: string;
}) {
  return (
    <div className="min-w-0 flex-1 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] p-3.5">
      <div
        className="mb-1.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-white"
        style={{ background: cor }}
      >
        {eyebrow}
      </div>
      <div className="font-mono text-[12.5px] text-[var(--fg-muted)]">{meta.codigo ?? '—'}</div>
      <div className="mb-2 break-words [overflow-wrap:anywhere] text-[13px] font-semibold text-[var(--fg)]">
        {meta.objeto}
      </div>
      <dl className="flex flex-col gap-1 text-[12px]">
        <div className="flex justify-between gap-2">
          <dt className="text-[var(--fg-muted)]">Modalidade</dt>
          <dd className="text-right font-medium text-[var(--fg)]">{meta.modalidade ?? '—'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-[var(--fg-muted)]">Valor estimado</dt>
          <dd className="text-right font-mono text-[var(--fg)]">{fmtMoney(meta.valor_estimado)}</dd>
        </div>
        <div className="flex items-center justify-between gap-2">
          <dt className="text-[var(--fg-muted)]">Fase</dt>
          <dd>
            <Chip variant="neutral" dot>
              {meta.status_label}
            </Chip>
          </dd>
        </div>
      </dl>
    </div>
  );
}

export default function ComparacaoPrecedenteModal({
  atual,
  precedente,
  onAbrirPrecedente,
  onClose,
}: {
  atual: string;
  precedente: string;
  onAbrirPrecedente?: () => void;
  onClose: () => void;
}) {
  const [data, setData] = useState<Comparacao | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    setData(null);
    setErro(false);
    buscarComparacaoPrecedente(atual, precedente)
      .then((d) => vivo && setData(d))
      .catch(() => vivo && setErro(true));
    return () => {
      vivo = false;
    };
  }, [atual, precedente]);

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="text-[11.5px] text-[var(--fg-muted)]">
        {data && !data.embeddings_ok && 'Similaridade textual indisponível — comparação básica.'}
      </span>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
        {onAbrirPrecedente && (
          <Button variant="default" icon="pi pi-external-link" onClick={onAbrirPrecedente}>
            Abrir precedente
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal open onClose={onClose} size="xl" title="Comparação com precedente" footer={footer}>
      {erro ? (
        <div className="py-10 text-center text-sm text-[var(--fg-muted)]">
          <i className="pi pi-exclamation-triangle mb-2 block text-xl text-[var(--warning-600)]" aria-hidden="true" />
          Não foi possível carregar a comparação.
        </div>
      ) : !data ? (
        <div className="py-10 text-center text-sm text-[var(--fg-muted)]">
          <i className="pi pi-spin pi-spinner mb-2 block text-xl" aria-hidden="true" />
          Analisando precedente…
        </div>
      ) : (
        <div className="flex max-h-[68vh] flex-col gap-5 overflow-y-auto">
          {/* Overview comparativo */}
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <MetaCard meta={data.atual} eyebrow="Processo atual" cor="var(--brand-600)" />
            <div className="flex shrink-0 flex-col items-center px-1">
              <i className="pi pi-arrows-h text-[var(--fg-muted)]" aria-hidden="true" />
              <div className="text-[18px] font-bold text-[var(--ai-600)]">
                {data.similaridade_pct != null ? `${data.similaridade_pct}%` : '—'}
              </div>
              <div className="text-[10px] uppercase tracking-[0.04em] text-[var(--fg-muted)]">
                similaridade
              </div>
            </div>
            <MetaCard meta={data.precedente} eyebrow="Precedente (concluído)" cor="var(--success-600)" />
          </div>

          {/* Recomendação IA — contagem real de seções reutilizáveis */}
          <div className="flex items-start gap-2.5 rounded-lg border border-[var(--ai-100)] bg-[var(--ai-50)] p-3">
            <i className="pi pi-sparkles mt-0.5 text-[var(--ai-500)]" aria-hidden="true" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ai-600)]">
                Recomendação IA
              </div>
              {data.resumo.total_campos > 0 ? (
                <div className="text-[13px] text-[var(--fg)]">
                  <b className="text-[var(--success-700)]">
                    {data.resumo.reusaveis} de {data.resumo.total_campos}
                  </b>{' '}
                  seções do precedente são idênticas ou diretamente reutilizáveis.
                </div>
              ) : (
                <div className="text-[13px] text-[var(--fg-muted)]">
                  O precedente ainda não tem documentos estruturados para comparar seção a seção.
                </div>
              )}
            </div>
          </div>

          {/* Seções comparadas */}
          {data.secoes.map((s) => (
            <details key={s.documento} open className="rounded-lg border border-[var(--border)]">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5">
                <i className="pi pi-chevron-right text-[11px] text-[var(--fg-muted)] transition-transform [details[open]>summary>&]:rotate-90" aria-hidden="true" />
                <span className="text-[13px] font-semibold text-[var(--fg)]">{s.documento}</span>
                <div className="ml-auto flex items-center gap-2">
                  {s.similaridade_pct != null && (
                    <>
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[var(--bg-page)]">
                        <div
                          className="h-full rounded-full bg-[var(--ai-500)]"
                          style={{ width: `${s.similaridade_pct}%` }}
                        />
                      </div>
                      <span className="font-mono text-[11.5px] text-[var(--fg-muted)]">
                        {s.similaridade_pct}%
                      </span>
                    </>
                  )}
                </div>
              </summary>
              <div className="overflow-x-auto border-t border-[var(--border)]">
                <table className="w-full min-w-[640px] border-collapse text-[12.5px]">
                  <thead>
                    <tr className="bg-[var(--bg-page)] text-left text-[11px] uppercase tracking-[0.04em] text-[var(--fg-muted)]">
                      <th className="w-[180px] px-3 py-2 font-semibold">Seção</th>
                      <th className="px-3 py-2 font-semibold">Processo atual</th>
                      <th className="px-3 py-2 font-semibold">Precedente</th>
                      <th className="w-[120px] px-3 py-2 font-semibold">Sugestão IA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.campos.map((c, ci) => {
                      const m = MATCH_META[c.match];
                      return (
                        <tr key={ci} className="border-t border-[var(--border)] align-top">
                          <td className="px-3 py-2">
                            <b className="break-words [overflow-wrap:anywhere] text-[12.5px] text-[var(--fg)]">
                              {c.campo}
                            </b>
                          </td>
                          <td className="px-3 py-2 text-[var(--fg)]">
                            {c.atual_vazio ? (
                              <i className="italic text-[var(--fg-muted)]">vazio</i>
                            ) : (
                              c.atual
                            )}
                          </td>
                          <td className="px-3 py-2 text-[var(--fg)]">{c.precedente}</td>
                          <td className="px-3 py-2">
                            <Chip variant={m.variant} dot>
                              <i className={`pi ${m.icon} mr-1 text-[9px]`} aria-hidden="true" />
                              {m.label}
                            </Chip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          ))}

          {/* Lições do precedente */}
          {data.licoes.length > 0 && (
            <div className="rounded-lg border border-[var(--border)] p-3.5">
              <h4 className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-[var(--fg)]">
                <i className="pi pi-history text-[var(--fg-muted)]" aria-hidden="true" />
                Lições do precedente
              </h4>
              <ul className="flex flex-col gap-2">
                {data.licoes.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12.5px] text-[var(--fg)]">
                    <i
                      className={`pi ${LICAO_ICON[l.tipo] ?? 'pi-circle-fill'} mt-0.5 text-[11px] text-[var(--fg-muted)]`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 break-words">{l.texto}</span>
                    <span className="shrink-0 text-[11px] text-[var(--fg-subtle)]">{l.quando}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
